import { OrderFilterData, OrderFilterTypes } from '@api/types';
import { TextCodes } from '@api/types/translation/TextCodes';
import { Button, CalendarField, DropdownField, DropdownOption, SearchField } from '@components';
import { useAppDispatch, useAppSelector } from '@hooks';
import { Clear } from '@mui/icons-material';
import { Box, Stack } from '@mui/material';
import { getTableBlockData, setFilters, setOffset } from '@store/table/table.slice';
import { t } from '@translations';
import debounce from 'lodash/debounce';
import { useCallback, useEffect, useMemo, useState } from 'react';

export type OrderListSearchProps = { id: string };

export const OrderListSearch = ({ id }: OrderListSearchProps) => {
  const orderStatusDropdownOptions: DropdownOption<string>[] = [
    { value: 'Awaiting', label: t(TextCodes.Order.Status.Awaiting) },
    { value: 'OrderProcessing', label: t(TextCodes.Order.Status.OrderProcessing) },
    { value: 'Rejected', label: t(TextCodes.Order.Status.Rejected) },
    { value: 'PartiallySent', label: t(TextCodes.Order.Status.PartiallySent) },
    { value: 'Sent', label: t(TextCodes.Order.Status.Sent) },
    { value: 'PartiallyInvoiced', label: t(TextCodes.Order.Status.PartiallyInvoiced) },
    { value: 'Invoiced', label: t(TextCodes.Order.Status.Invoiced) },
  ];

  const orderTypeDropdownOptions: DropdownOption<string>[] = [
    { value: 'DraftOrder', label: t(TextCodes.Order.Type.Draft) },
    { value: 'NormalOrder', label: t(TextCodes.Order.Type.Normal) },
    { value: 'SeasonalDraftOrder', label: t(TextCodes.Order.Type.SeasonalDraft) },
    { value: 'SeasonalOrder', label: t(TextCodes.Order.Type.Seasonal) },
    { value: 'WarrantyDraftOrder', label: t(TextCodes.Order.Type.WarrantyDraft) },
    { value: 'WarrantyRejectedOrder', label: t(TextCodes.Order.Type.WarrantyRejected) },
    { value: 'WarrantyOrder', label: t(TextCodes.Order.Type.Warranty) },
  ];

  const dispatch = useAppDispatch();
  const { filters, visibleFilters } = useAppSelector(getTableBlockData(id));

  const [localFilters, setLocalFilters] = useState(filters as OrderFilterData);

  const debouncedDispatch = useMemo(
    () =>
      debounce((updatedFilters: OrderFilterData) => {
        dispatch(setFilters({ id, filters: updatedFilters }));
      }, 500),
    [dispatch, id]
  );

  const updateFilters = useCallback(
    (field: keyof OrderFilterData, value: any) => {
      const updated = { ...localFilters, [field]: value };
      setLocalFilters(updated);
      debouncedDispatch(updated);
    },
    [localFilters, debouncedDispatch]
  );

  useEffect(() => {
    setLocalFilters(filters as OrderFilterData);
  }, [filters]);

  useEffect(() => {
    return () => {
      debouncedDispatch.cancel();
    };
  }, [debouncedDispatch]);

  const isFilterVisible = (filterName: OrderFilterTypes) => visibleFilters.includes(filterName);

  const clearFilters = () => {
    const newFilters: OrderFilterData = { ...localFilters };
    visibleFilters.forEach((filter) => {
      newFilters[filter as keyof OrderFilterData] = undefined;
    });
    setLocalFilters(newFilters);
    dispatch(setOffset({ id, offset: 0 }));
    debouncedDispatch(newFilters);
  };

  return (
    <Box sx={{ mb: 4 }}>
      <Stack direction={{ xs: 'column', lg: 'row' }} spacing={{ xs: 4, lg: 2 }}>
        {isFilterVisible('Search') && (
          <SearchField
            label={t(TextCodes.Order.Block.Search)}
            name='filter'
            value={localFilters?.Search || ''}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              updateFilters('Search', e.target.value)
            }
            sx={{ width: '100%' }}
            placeholder={t(TextCodes.Order.Block.SearchPlaceholder)}
          />
        )}
        {isFilterVisible('ClientStatus') && (
          <DropdownField
            label={t(TextCodes.Order.Block.OrderStatus)}
            name='orderStatus'
            options={orderStatusDropdownOptions}
            value={localFilters?.ClientStatus}
            multiple
            getValue={(value: string | string[]) => updateFilters('ClientStatus', value)}
          />
        )}
        {isFilterVisible('OrderType') && (
          <DropdownField
            label={t(TextCodes.Order.Block.OrderType)}
            name='orderType'
            options={orderTypeDropdownOptions}
            value={localFilters?.OrderType}
            multiple
            getValue={(value: string | string[]) => updateFilters('OrderType', value)}
          />
        )}
        {isFilterVisible('CreatedOn') && (
          <CalendarField
            value={localFilters?.CreatedOn || ''}
            onChange={(e: any) => {
              updateFilters('CreatedOn', e);
            }}
            disableFuture
            label={t(TextCodes.Order.Block.CreatedOn)}
          />
        )}
        <Button variant='tertiary' onClick={clearFilters} startIcon={<Clear />}>
          {t(TextCodes.Order.Block.Clear)}
        </Button>
      </Stack>
    </Box>
  );
};
